import { Button, Card } from "@120wateraudit/waterworks";
import React, { Fragment, useContext } from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";

import { LoadingIndicatorPage } from "src/components/LoadingIndicatorPage";
import NotFoundCard from "src/components/NotFoundCard";
import Page from "src/components/Page";
import Row from "src/components/Row";
import { Media } from "src/modules/App/Media";
// import BasicContactCard from "src/modules/Contacts/Card";
import { ModalContext } from "src/modules/Modal";
import { CustomerNoticeSubmissionTable } from "src/modules/Systems/CustomerNoticeSubmissionTable";
import {
  useGetAllSystemContactsQuery,
  useGetAllTerritoriesQuery,
  useGetSingleSystemQuery,
} from "src/services";
import { useGetAssetStatsQuery } from "src/services/pws-api";
import { formatOneLineAddress } from "src/utils/format";
import PageHeader from "src/components/PageHeader";
import { isWrite } from "src/modules/User";
import { TerritoryChangeDropdown } from "src/modules/Systems/TerritoryChangeDropdown";
import { getEnvironment } from "src/utils/getEnvironment";
import { SubnavigationTabs } from "src/components/SubnavigationTabs/SubnavigationTabs";
import UtilityDetailsInfo from "./Utilities/UtilityDetailsInfo";

const UtilityDetailsCustomerNoticeCertificationPage: React.FC = () => {
  const { id: systemId } = useParams<{ id: string }>();
  const { openModal } = useContext(ModalContext);
  const openAddContactDrawer = (): void => {
    openModal("addContact", { systemId }, "drawer");
  };

  const {
    data: systemDetailResponse,
    isError,
    isFetching,
    refetch: refetchSystemDetails,
  } = useGetSingleSystemQuery(systemId ?? "");
  const { data: systemContacts } = useGetAllSystemContactsQuery(systemId ?? "");
  const { data } = useGetAssetStatsQuery(
    systemDetailResponse?.data.accountId ?? 0,
    { skip: systemDetailResponse?.data.accountId === undefined }
  );

  const ENV = getEnvironment();
  const BASE_PTD_URL =
    ENV === "production"
      ? "https://pws-ptd.120wateraudit.com/"
      : ENV === "staging"
      ? "https://pws-ptd-staging.120wateraudit.com/"
      : "https://pws-ptd-dev.120wateraudit.com/";

  const {
    galvanizedAssets = 0,
    leadAssets = 0,
    nonLeadAssets = 0,
    unknownAssets = 0,
  } = data ?? {};

  const { data: territories = [] } = useGetAllTerritoriesQuery({});

  if (isFetching) {
    return <LoadingIndicatorPage />;
  }

  if (!systemDetailResponse?.data || isError) {
    // TODO add back button
    return (
      <Page>
        <NotFoundCard>
          <p>
            We could not find the Utility you were looking for. It may have been
            moved or marked as inactive.
          </p>
          <p>
            We recommend returning to{" "}
            <Link to="/utilities">
              the list of Utilities for your account and searching for it by
              name.
            </Link>
          </p>
        </NotFoundCard>
      </Page>
    );
  }

  const {
    data: { name, territory, subdomain, active, ...address },
  } = systemDetailResponse;
  const { data: contacts = [] } = systemContacts ?? {};

  return (
    <Page
      actions={
        isWrite() ? (
          <Button onClick={openAddContactDrawer} variant="primary">
            Create Contact
          </Button>
        ) : (
          <Fragment />
        )
      }
      hasBackButton
      title={
        <div>
          <div style={{ display: "flex" }}>
            <PageHeader style={{ paddingRight: "15px", paddingLeft: "5px" }}>
              {name ?? "--"}
            </PageHeader>
            {systemId && (
              <TerritoryChangeDropdown
                territories={territories}
                currentTerritory={territory}
                systemId={systemId}
                refetchSystemDetails={refetchSystemDetails}
              />
            )}
          </div>
          <h2 style={{ paddingLeft: "5px" }}>
            {formatOneLineAddress(address)}
          </h2>
          {subdomain && active && (
            <span>
              PWS PTD:{" "}
              <a
                href={`${BASE_PTD_URL}${subdomain}`}
                target="_blank"
                rel="noreferrer"
              >
                {BASE_PTD_URL}
                {subdomain}
              </a>
            </span>
          )}
        </div>
      }
    >
      <Media greaterThanOrEqual="md" style={{ display: "flex" }}>
        <Header style={{ paddingBottom: "1.5rem" }}>
          <UtilityDetailsInfo
            systemDetailResponse={systemDetailResponse}
            contacts={contacts}
            galvanizedAssets={galvanizedAssets}
            leadAssets={leadAssets}
            nonLeadAssets={nonLeadAssets}
            unknownAssets={unknownAssets}
          />
        </Header>
      </Media>
      <Content>
        <div
          style={{
            flex: 3,
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <SubnavigationTabs
            subnavigationTabs={[
              {
                label: "Inventory",
                to: systemId ? `/utilities/${systemId}` : "utilities",
              },
              {
                label: "Customer Notice Certifications",
                to: systemId
                  ? `/utilities/${systemId}/customerNoticeCertification`
                  : "utilities",
              },
            ]}
          />
          <Card>
            <CustomerNoticeSubmissionTable
              systemId={systemId ?? ""}
              isOnUtilityDetailsPage={true}
            />
          </Card>
        </div>
      </Content>
    </Page>
  );
};

const Content = styled(Row)`
  gap: 1rem;
  align-items: start;
  flex-wrap: wrap-reverse;
`;

const Header = styled(Row)`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex: 3;
`;

export default UtilityDetailsCustomerNoticeCertificationPage;
